import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import ErrorTable from '../ErrorTable';
import { useBulkStockUpload } from '../useBulkStockUpload';
import type { Product } from '../../../types';
import type { ValidationError } from '../useBulkStockUpload';

interface OrgSku {
  sku: string;
  skuCostOverride: number | null;
  id: string;
}

interface ValidationStepProps {
  products: Product[];
  allSkus: OrgSku[];
}

const ValidationStep: React.FC<ValidationStepProps> = ({ products, allSkus }) => {
  const {
    parsedRows,
    validationErrors,
    blockOnErrors,
    setValidationErrors,
    setBlockOnErrors,
    setPreviewRows,
    setSkippedRows,
    setStep,
  } = useBulkStockUpload();

  const [hasRun, setHasRun] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);

  // Build a product lookup from all known products at this location
  const productMap = useMemo(
    () => new Map(products.map((p) => [p.sku, p])),
    [products]
  );

  // Build an org-wide SKU lookup for cost data on new-at-location SKUs
  const allSkuMap = useMemo(
    () => new Map(allSkus.map((s) => [s.sku, s])),
    [allSkus]
  );

  // Run client-side validation once on mount
  useEffect(() => {
    if (hasRun) return;
    setHasRun(true);

    const errors: ValidationError[] = [];
    const seenSkus = new Set<string>();

    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];
      if (!row) continue;
      const csvRow = i + 2; // 1-indexed + header row

      if (!row.sku || row.sku.trim() === '') {
        errors.push({ row: csvRow, sku: row.sku || '', reason: 'SKU is empty' });
        continue;
      }

      const normalizedSku = row.sku.trim().toUpperCase();
      if (seenSkus.has(normalizedSku)) {
        errors.push({ row: csvRow, sku: row.sku, reason: 'Duplicate SKU in file' });
        continue;
      }
      seenSkus.add(normalizedSku);

      if (row.stock <= 0) {
        errors.push({ row: csvRow, sku: row.sku, reason: 'Quantity must be greater than 0' });
        continue;
      }

      if (row.stock > 999999) {
        errors.push({ row: csvRow, sku: row.sku, reason: 'Quantity exceeds maximum (999,999)' });
        continue;
      }

      // Check if SKU exists in the org at all (not just this location)
      // Note: products list from useInventory contains SKUs at the current location only.
      // SKUs that exist in the org but not at this location will get auto-activated server-side.
      // We don't flag those as errors — they will show as "New at location" in preview.
      // Truly unknown SKUs will be caught server-side during bulkCsvImport.
    }

    setValidationErrors(errors);

    // Auto-expand if few errors
    if (errors.length > 0 && errors.length < 10) {
      setErrorsOpen(true);
    }
  }, [hasRun, parsedRows, productMap, setValidationErrors]);

  // Auto-advance if no errors (with brief delay so user sees the success state)
  useEffect(() => {
    if (hasRun && validationErrors.length === 0 && parsedRows.length > 0) {
      const timer = setTimeout(buildPreviewAndAdvance, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRun, validationErrors.length]);

  const errorSkus = useMemo(
    () => new Set(validationErrors.map((e) => e.sku)),
    [validationErrors]
  );

  const validRowCount = parsedRows.filter(
    (r) => !errorSkus.has(r.sku)
  ).length;

  const buildPreviewAndAdvance = () => {
    const errorSkuSet = new Set(validationErrors.map((e) => e.sku));
    const validRows = parsedRows.filter((r) => !errorSkuSet.has(r.sku));
    const skipped = parsedRows.filter((r) => errorSkuSet.has(r.sku));

    const preview = validRows.map((row) => {
      const product = productMap.get(row.sku);
      const orgSku = allSkuMap.get(row.sku);
      const currentStock = product?.stockLevel ?? 0;
      // Use location-level effectiveCost first, fall back to org-wide skuCostOverride
      const unitCost = product?.effectiveCost ?? orgSku?.skuCostOverride ?? null;
      const isNewAtLocation = !product;

      return {
        sku: row.sku,
        currentStock,
        qtyToAdd: row.stock,
        newTotal: currentStock + row.stock,
        unitCost,
        totalCostImpact: unitCost != null ? unitCost * row.stock : null,
        isNewAtLocation,
        skuId: product?.id ?? orgSku?.id,
      };
    });

    setPreviewRows(preview);
    setSkippedRows(skipped);
    setStep('preview');
  };

  const handleContinue = () => {
    buildPreviewAndAdvance();
  };

  const handleChangeFile = () => {
    setStep('upload');
    setValidationErrors([]);
  };

  // If still running validation, show nothing extra
  if (!hasRun) return null;

  // No errors — brief green flash before auto-advance
  if (validationErrors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <CheckCircle2 className="h-10 w-10 text-green-500" />
        <p className="text-sm font-medium text-green-700">
          All {parsedRows.length} rows valid
        </p>
        <p className="text-xs text-muted-foreground">Preparing preview...</p>
      </div>
    );
  }

  // Errors present
  return (
    <div className="space-y-4">
      {/* Summary */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>{validationErrors.length}</strong> of{' '}
          <strong>{parsedRows.length}</strong> rows have errors
        </AlertDescription>
      </Alert>

      {/* Error mode toggle */}
      <div className="flex flex-col gap-1.5 p-3 rounded-md border bg-muted/30">
        <div className="flex items-center gap-3">
          <Switch
            id="error-mode"
            checked={!blockOnErrors}
            onCheckedChange={(checked) => setBlockOnErrors(!checked)}
          />
          <Label htmlFor="error-mode" className="text-sm cursor-pointer">
            Continue with valid rows
          </Label>
        </div>
        <p className="text-xs text-muted-foreground pl-[44px]">
          {blockOnErrors
            ? 'Upload blocked: fix all errors first'
            : `${validRowCount} valid row${validRowCount !== 1 ? 's' : ''} will be imported, ${validationErrors.length} row${validationErrors.length !== 1 ? 's' : ''} will be skipped`}
        </p>
      </div>

      {/* Collapsible error details */}
      <Collapsible open={errorsOpen} onOpenChange={setErrorsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between px-3"
          >
            <span className="text-sm">
              {errorsOpen ? 'Hide' : 'Show'} {validationErrors.length} error
              {validationErrors.length !== 1 ? 's' : ''}
            </span>
            {errorsOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <ErrorTable errors={validationErrors} />
        </CollapsibleContent>
      </Collapsible>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={handleChangeFile}>
          Change File
        </Button>
        {!blockOnErrors && validRowCount > 0 && (
          <Button
            onClick={handleContinue}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            Continue with {validRowCount} valid rows
          </Button>
        )}
      </div>
    </div>
  );
};

export default ValidationStep;
